import { useState, useCallback, useRef } from "react";
import { useRole } from "@/contexts/RoleContext";
import { apiClient } from "@/services/apiClient";
import { createPortalClient, PortalClient } from "@/services/portalClient";
import { UploadContentType } from "@/core/types/mediaContentType";
import { putToGCS } from "@/core/upload/putToGCS";

export type UploadStatus = "pending" | "uploading" | "complete" | "failed";

export interface FileUploadStatus {
  file: File;
  status: UploadStatus;
  progress: number;
  mediaItemId?: number;
  error?: string;
}

export interface UploadResult {
  success: boolean;
  mediaItemId?: number;
  status?: string;
  message?: string;
  processingTriggered?: boolean;
  error?: string;
}

interface InitiateResponse {
  uploadUrl: string;
  blobPath: string;
  storageId: string;
  contentTypeHeader: string;
}

interface CompleteResponse {
  success: boolean;
  mediaItemId?: number;
  processingTriggered?: boolean;
}

export interface UseMediaUploadOptions {
  clientId: string;
  /**
   * Content type for uploads:
   * - "photos": image files only
   * - "stories": video or image files
   * - "reels", "videos": video files only
   * - "auto": Auto-detect from file extension (images->photos, videos->videos)
   */
  contentType: UploadContentType;
  /** When provided, the finalizer job links uploaded items to this collection */
  collectionId?: string;
  onUploadComplete?: (result: UploadResult) => void;
  onAllComplete?: () => void;
  onError?: (file: File, error: string) => void;
}

const IMAGE_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".bmp",
  ".heic",
]);
const VIDEO_EXTENSIONS = new Set([".mp4", ".mov", ".avi", ".mkv", ".webm"]);
const AUDIO_EXTENSIONS = new Set([
  ".mp3",
  ".wav",
  ".aac",
  ".flac",
  ".ogg",
  ".m4a",
  ".wma",
]);

const MAX_IMAGE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024 * 1024; // 50GB
const MAX_AUDIO_SIZE = 100 * 1024 * 1024; // 100MB

// Files larger than this use signed-URL upload to bypass Cloud Run's 32 MiB body limit
const SIGNED_URL_THRESHOLD = 30 * 1024 * 1024; // 30MB

// Files larger than this use resumable chunked upload for reliability
const RESUMABLE_THRESHOLD = 100 * 1024 * 1024; // 100MB
const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

const MIME_TYPE_MAP: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".bmp": "image/bmp",
  ".heic": "image/heic",
  ".mp4": "video/mp4",
  ".mov": "video/quicktime",
  ".avi": "video/x-msvideo",
  ".mkv": "video/x-matroska",
  ".webm": "video/webm",
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
  ".aac": "audio/aac",
  ".flac": "audio/flac",
  ".ogg": "audio/ogg",
  ".m4a": "audio/mp4",
  ".wma": "audio/x-ms-wma",
};

function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  return lastDot >= 0 ? filename.substring(lastDot).toLowerCase() : "";
}

function getContentTypeForFile(file: File): "photos" | "videos" | "audio" {
  const ext = getFileExtension(file.name);
  if (IMAGE_EXTENSIONS.has(ext)) return "photos";
  if (AUDIO_EXTENSIONS.has(ext)) return "audio";
  return "videos";
}

const STORY_ASPECT_RATIO = 9 / 16;
const STORY_ASPECT_TOLERANCE = 0.02;

async function validateStoryImageAspectRatio(
  file: File
): Promise<{ valid: boolean; error?: string }> {
  const url = URL.createObjectURL(file);
  try {
    const { width, height } = await new Promise<{ width: number; height: number }>(
      (resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => reject(new Error("Could not read image"));
        img.src = url;
      }
    );
    if (!width || !height) {
      return { valid: false, error: "Could not determine image dimensions" };
    }
    const ratio = width / height;
    if (Math.abs(ratio - STORY_ASPECT_RATIO) > STORY_ASPECT_TOLERANCE) {
      return {
        valid: false,
        error: `Story images must be 9:16 (got ${width}x${height})`,
      };
    }
    return { valid: true };
  } catch (e) {
    return { valid: false, error: "Could not read image for aspect-ratio check" };
  } finally {
    URL.revokeObjectURL(url);
  }
}

const POST_VIDEO_ASPECT_RATIO = 4 / 5;
const POST_VIDEO_ASPECT_TOLERANCE = 0.02;

async function validatePostVideoAspectRatio(
  file: File
): Promise<{ valid: boolean; error?: string }> {
  const url = URL.createObjectURL(file);
  try {
    const { width, height } = await new Promise<{ width: number; height: number }>(
      (resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () =>
          resolve({ width: video.videoWidth, height: video.videoHeight });
        video.onerror = () => reject(new Error("Could not read video"));
        video.src = url;
      }
    );
    if (!width || !height) {
      return { valid: false, error: "Could not determine video dimensions" };
    }
    const ratio = width / height;
    if (Math.abs(ratio - POST_VIDEO_ASPECT_RATIO) > POST_VIDEO_ASPECT_TOLERANCE) {
      return { valid: false, error: `Post videos must be 4:5 (got ${width}x${height})` };
    }
    return { valid: true };
  } catch (e) {
    return { valid: false, error: "Could not read video for aspect-ratio check" };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function validateFile(
  file: File,
  contentType: string
): { valid: boolean; error?: string; resolvedContentType?: "photos" | "videos" | "audio" } {
  const ext = getFileExtension(file.name);
  const isImage = IMAGE_EXTENSIONS.has(ext);
  const isVideo = VIDEO_EXTENSIONS.has(ext);
  const isAudio = AUDIO_EXTENSIONS.has(ext);

  if (!isImage && !isVideo && !isAudio) {
    return {
      valid: false,
      error: `Unsupported file type: ${ext}`,
    };
  }

  // Auto mode: determine content type from file
  if (contentType === "auto") {
    const resolvedContentType = getContentTypeForFile(file);
    const maxSize = isVideo ? MAX_VIDEO_SIZE : isAudio ? MAX_AUDIO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) {
      const maxMb = Math.round(maxSize / (1024 * 1024));
      return {
        valid: false,
        error: `File too large. Maximum size is ${maxMb}MB`,
      };
    }
    return { valid: true, resolvedContentType };
  }

  // Strict mode: validate content type match
  if (contentType === "photos" && !isImage) {
    return { valid: false, error: "Expected an image file for photos" };
  }

  if (["reels", "videos", "post_video"].includes(contentType) && !isVideo) {
    return {
      valid: false,
      error: `Expected a video file for ${contentType}`,
    };
  }

  if (contentType === "stories" && !isVideo && !isImage) {
    return {
      valid: false,
      error: "Expected a video or image file for stories",
    };
  }

  if (contentType === "audio" && !isAudio) {
    return { valid: false, error: "Expected an audio file" };
  }

  const maxSize = isVideo ? MAX_VIDEO_SIZE : isAudio ? MAX_AUDIO_SIZE : MAX_IMAGE_SIZE;
  if (file.size > maxSize) {
    const maxMb = Math.round(maxSize / (1024 * 1024));
    return {
      valid: false,
      error: `File too large. Maximum size is ${maxMb}MB`,
    };
  }

  return { valid: true };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Upload a single chunk to a GCS resumable session URI with retry.
 * Retries on network errors and 503 (rate limit) with exponential backoff.
 */
async function uploadChunkWithRetry(
  sessionUri: string,
  chunk: Blob,
  contentRange: string,
  mimeType: string,
  signal: AbortSignal,
  maxRetries = 5,
): Promise<Response> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(sessionUri, {
        method: "PUT",
        headers: {
          "Content-Range": contentRange,
          "Content-Type": mimeType,
        },
        body: chunk,
        signal,
      });
      if (response.status === 503) {
        await sleep(Math.pow(2, attempt) * 1000);
        continue;
      }
      return response;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (signal.aborted) throw lastError;
      await sleep(Math.pow(2, attempt) * 1000);
    }
  }
  throw lastError ?? new Error("Chunk upload failed after retries");
}

/**
 * Query a GCS resumable session URI for the last committed byte offset.
 * Used to resume uploads after a failure.
 */
async function queryResumeOffset(sessionUri: string): Promise<number> {
  try {
    const response = await fetch(sessionUri, {
      method: "PUT",
      headers: {
        "Content-Range": "bytes */*",
        "Content-Length": "0",
      },
    });
    if (response.status === 308) {
      const range = response.headers.get("Range");
      if (range) {
        return parseInt(range.split("-")[1], 10) + 1;
      }
    }
  } catch {
    // If we can't query, start from zero
  }
  return 0;
}

export function useMediaUpload(options: UseMediaUploadOptions) {
  const { clientId, contentType, collectionId, onUploadComplete, onAllComplete, onError } =
    options;

  const { role } = useRole();
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const abortControllersRef = useRef<Map<File, AbortController>>(new Map());
  const portalClientRef = useRef<PortalClient | null>(null);

  const getClient = useCallback(() => {
    if (role === "admin") {
      return apiClient;
    }
    if (!portalClientRef.current) {
      portalClientRef.current = createPortalClient(clientId, role === "editor" ? "editor" : "client");
    }
    return portalClientRef.current;
  }, [role, clientId]);

  const updateFileStatus = useCallback(
    (file: File, update: Partial<FileUploadStatus>) => {
      setFileStatuses((prev) =>
        prev.map((fs) => (fs.file === file ? { ...fs, ...update } : fs))
      );
    },
    []
  );

  /**
   * Upload via the existing direct multipart endpoint.
   * Used for files under the signed-URL threshold.
   */
  const uploadFileDirect = useCallback(
    async (file: File, fileContentType?: "photos" | "videos" | "audio"): Promise<UploadResult> => {
      const controller = new AbortController();
      abortControllersRef.current.set(file, controller);

      const effectiveContentType = fileContentType || (contentType === "auto" ? getContentTypeForFile(file) : contentType);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("content_type", effectiveContentType);
        formData.append("client_id", clientId);
        if (collectionId) {
          formData.append("collection_id", collectionId);
        }

        const client = getClient();
        return await client.post<UploadResult>(
          "/api/portal/upload",
          formData,
          {
            headers: { "Content-Type": undefined },
            signal: controller.signal,
            timeout: 300000,
          },
        );
      } finally {
        abortControllersRef.current.delete(file);
      }
    },
    [clientId, contentType, collectionId, getClient]
  );

  /**
   * Upload via signed URL: initiate -> PUT to GCS -> complete.
   * Used for files over the signed-URL threshold (bypasses Cloud Run body limit).
   */
  const uploadFileSignedUrl = useCallback(
    async (file: File, fileContentType?: "photos" | "videos" | "audio"): Promise<UploadResult> => {
      const controller = new AbortController();
      abortControllersRef.current.set(file, controller);

      const effectiveContentType = fileContentType || (contentType === "auto" ? getContentTypeForFile(file) : contentType);
      const client = getClient();

      try {
        // Step 1: Initiate - get signed URL from backend
        const initiateBody: Record<string, string | number> = {
          filename: file.name,
          contentType: effectiveContentType,
          fileSize: file.size,
        };
        if (role === "admin") {
          initiateBody.clientId = clientId;
        }

        const initResponse = await client.post<InitiateResponse>(
          "/api/portal/upload/initiate",
          initiateBody,
          { signal: controller.signal },
        );

        // Step 2: PUT directly to GCS with real progress tracking
        const ext = getFileExtension(file.name);
        const mimeType = MIME_TYPE_MAP[ext] || "application/octet-stream";

        await putToGCS(
          initResponse.uploadUrl,
          file,
          mimeType,
          (fraction) => {
            updateFileStatus(file, { progress: Math.round(fraction * 95) });
          },
          controller.signal,
        );

        updateFileStatus(file, { progress: 97 });

        // Step 3: Complete - tell backend to verify blob, create record, trigger processing
        const completeBody: Record<string, string | number> = {
          storageId: initResponse.storageId,
          filename: file.name,
          contentType: effectiveContentType,
          fileSize: file.size,
        };
        if (role === "admin" || role === "editor") {
          completeBody.clientId = clientId;
        }
        if (collectionId) {
          completeBody.collectionId = collectionId;
        }

        const completeResponse = await client.post<CompleteResponse>(
          "/api/portal/upload/complete",
          completeBody,
          { signal: controller.signal },
        );

        return {
          success: completeResponse.success,
          mediaItemId: completeResponse.mediaItemId,
          processingTriggered: completeResponse.processingTriggered,
        };
      } finally {
        abortControllersRef.current.delete(file);
      }
    },
    [clientId, contentType, collectionId, role, getClient, updateFileStatus]
  );

  /**
   * Upload via GCS resumable session: initiate -> chunked PUT -> complete.
   * Used for large files (>100MB). Uploads in 8MB chunks with per-chunk
   * retry and resume-from-failure support.
   */
  const uploadFileResumable = useCallback(
    async (file: File, fileContentType?: "photos" | "videos" | "audio"): Promise<UploadResult> => {
      const controller = new AbortController();
      abortControllersRef.current.set(file, controller);

      const effectiveContentType = fileContentType || (contentType === "auto" ? getContentTypeForFile(file) : contentType);
      const client = getClient();

      try {
        // Step 1: Initiate resumable session
        const initiateBody: Record<string, string | number> = {
          filename: file.name,
          contentType: effectiveContentType,
          fileSize: file.size,
        };
        if (role === "admin") {
          initiateBody.clientId = clientId;
        }

        const initResponse = await client.post<InitiateResponse>(
          "/api/portal/upload/initiate-resumable",
          initiateBody,
          { signal: controller.signal },
        );

        const sessionUri = initResponse.uploadUrl;
        const ext = getFileExtension(file.name);
        const mimeType = MIME_TYPE_MAP[ext] || "application/octet-stream";
        const totalSize = file.size;
        let offset = 0;

        // Step 2: Upload in chunks
        while (offset < totalSize) {
          if (controller.signal.aborted) {
            throw new Error("Upload cancelled");
          }

          const end = Math.min(offset + CHUNK_SIZE, totalSize);
          const chunk = file.slice(offset, end);
          const contentRange = `bytes ${offset}-${end - 1}/${totalSize}`;

          const response = await uploadChunkWithRetry(
            sessionUri,
            chunk,
            contentRange,
            mimeType,
            controller.signal,
          );

          if (response.status === 308) {
            // Chunk accepted, read committed range to determine next offset
            const range = response.headers.get("Range");
            offset = range ? parseInt(range.split("-")[1], 10) + 1 : end;
          } else if (response.ok) {
            // Final chunk accepted, upload complete
            offset = totalSize;
          } else {
            // Unexpected status - try to resume from last committed byte
            offset = await queryResumeOffset(sessionUri);
            if (offset >= totalSize) break;
            // Retry from the resumed offset on next loop iteration
            continue;
          }

          updateFileStatus(file, {
            progress: Math.round((offset / totalSize) * 95),
          });
        }

        updateFileStatus(file, { progress: 97 });

        // Step 3: Complete - same as signed URL flow
        const completeBody: Record<string, string | number> = {
          storageId: initResponse.storageId,
          filename: file.name,
          contentType: effectiveContentType,
          fileSize: file.size,
        };
        if (role === "admin" || role === "editor") {
          completeBody.clientId = clientId;
        }
        if (collectionId) {
          completeBody.collectionId = collectionId;
        }

        const completeResponse = await client.post<CompleteResponse>(
          "/api/portal/upload/complete",
          completeBody,
          { signal: controller.signal },
        );

        return {
          success: completeResponse.success,
          mediaItemId: completeResponse.mediaItemId,
          processingTriggered: completeResponse.processingTriggered,
        };
      } finally {
        abortControllersRef.current.delete(file);
      }
    },
    [clientId, contentType, collectionId, role, getClient, updateFileStatus]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      // Validate all files first, capturing resolved content type for auto mode
      const validatedFiles: { file: File; error?: string; resolvedContentType?: "photos" | "videos" | "audio" }[] = await Promise.all(
        files.map(async (file) => {
          const validation = validateFile(file, contentType);
          if (!validation.valid) {
            return { file, error: validation.error, resolvedContentType: validation.resolvedContentType };
          }
          if (contentType === "stories" && IMAGE_EXTENSIONS.has(getFileExtension(file.name))) {
            const ratio = await validateStoryImageAspectRatio(file);
            if (!ratio.valid) {
              return { file, error: ratio.error, resolvedContentType: validation.resolvedContentType };
            }
          }
          if (contentType === "post_video" && VIDEO_EXTENSIONS.has(getFileExtension(file.name))) {
            const ratio = await validatePostVideoAspectRatio(file);
            if (!ratio.valid) {
              return { file, error: ratio.error, resolvedContentType: validation.resolvedContentType };
            }
          }
          return { file, resolvedContentType: validation.resolvedContentType };
        })
      );

      // Initialize statuses
      const initialStatuses: FileUploadStatus[] = validatedFiles.map(
        ({ file, error }) => ({
          file,
          status: error ? "failed" : "pending",
          progress: 0,
          error,
        })
      );

      setFileStatuses(initialStatuses);
      setIsUploading(true);

      // Upload valid files sequentially
      const pendingFiles = validatedFiles.filter(({ error }) => !error);

      for (let i = 0; i < pendingFiles.length; i++) {
        const { file, resolvedContentType } = pendingFiles[i];

        updateFileStatus(file, { status: "uploading", progress: 0 });

        try {
          let result: UploadResult;

          if (file.size > RESUMABLE_THRESHOLD) {
            result = await uploadFileResumable(file, resolvedContentType);
          } else if (file.size > SIGNED_URL_THRESHOLD) {
            result = await uploadFileSignedUrl(file, resolvedContentType);
          } else {
            // For direct uploads, jump to 50% since we lack granular progress
            updateFileStatus(file, { progress: 50 });
            result = await uploadFileDirect(file, resolvedContentType);
          }

          updateFileStatus(file, {
            status: "complete",
            progress: 100,
            mediaItemId: result.mediaItemId,
          });

          onUploadComplete?.(result);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Upload failed";

          updateFileStatus(file, {
            status: "failed",
            progress: 0,
            error: errorMessage,
          });

          onError?.(file, errorMessage);
        }
      }

      setIsUploading(false);
      onAllComplete?.();
    },
    [contentType, uploadFileDirect, uploadFileSignedUrl, uploadFileResumable, updateFileStatus, onUploadComplete, onAllComplete, onError]
  );

  const cancelAll = useCallback(() => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
    setIsUploading(false);
    setFileStatuses((prev) =>
      prev.map((fs) =>
        fs.status === "uploading"
          ? { ...fs, status: "failed", error: "Cancelled" }
          : fs
      )
    );
  }, []);

  const cancelFile = useCallback((file: File) => {
    const controller = abortControllersRef.current.get(file);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(file);
    }
    setFileStatuses((prev) =>
      prev.map((fs) =>
        fs.file === file
          ? { ...fs, status: "failed", error: "Cancelled" }
          : fs
      )
    );
  }, []);

  const retryFile = useCallback(
    async (file: File) => {
      const validation = validateFile(file, contentType);
      if (!validation.valid) {
        setFileStatuses((prev) =>
          prev.map((fs) =>
            fs.file === file
              ? { ...fs, status: "failed", error: validation.error }
              : fs
          )
        );
        return;
      }

      if (contentType === "stories" && IMAGE_EXTENSIONS.has(getFileExtension(file.name))) {
        const ratio = await validateStoryImageAspectRatio(file);
        if (!ratio.valid) {
          setFileStatuses((prev) =>
            prev.map((fs) =>
              fs.file === file ? { ...fs, status: "failed", error: ratio.error } : fs
            )
          );
          return;
        }
      }

      if (contentType === "post_video" && VIDEO_EXTENSIONS.has(getFileExtension(file.name))) {
        const ratio = await validatePostVideoAspectRatio(file);
        if (!ratio.valid) {
          setFileStatuses((prev) =>
            prev.map((fs) =>
              fs.file === file ? { ...fs, status: "failed", error: ratio.error } : fs
            )
          );
          return;
        }
      }

      updateFileStatus(file, { status: "uploading", progress: 0, error: undefined });

      try {
        let result: UploadResult;

        if (file.size > RESUMABLE_THRESHOLD) {
          result = await uploadFileResumable(file, validation.resolvedContentType);
        } else if (file.size > SIGNED_URL_THRESHOLD) {
          result = await uploadFileSignedUrl(file, validation.resolvedContentType);
        } else {
          updateFileStatus(file, { progress: 50 });
          result = await uploadFileDirect(file, validation.resolvedContentType);
        }

        updateFileStatus(file, {
          status: "complete",
          progress: 100,
          mediaItemId: result.mediaItemId,
        });

        onUploadComplete?.(result);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";

        updateFileStatus(file, {
          status: "failed",
          progress: 0,
          error: errorMessage,
        });

        onError?.(file, errorMessage);
      }
    },
    [contentType, uploadFileDirect, uploadFileSignedUrl, uploadFileResumable, updateFileStatus, onUploadComplete, onError]
  );

  const clearCompleted = useCallback(() => {
    setFileStatuses((prev) => prev.filter((fs) => fs.status !== "complete"));
  }, []);

  const clearAll = useCallback(() => {
    cancelAll();
    setFileStatuses([]);
  }, [cancelAll]);

  return {
    uploadFiles,
    fileStatuses,
    isUploading,
    cancelAll,
    cancelFile,
    retryFile,
    clearCompleted,
    clearAll,
  };
}
